import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { ActionTarget } from '@shared/interfaces/actions';
import { MeetingRecipient } from '@shared/interfaces/meetings/meeting-recipients.interface';
import { PointRecipient } from '@shared/interfaces/points';
import { BaseContact } from '@shared/models/iContact/base-contact';
import { MeetingsService } from '@shared/services/meetings.service';
import { PointsService } from '@shared/services/points.service';
import { finalize } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { NotificationError, NotificationInfo, NotificationSuccess } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { IContactService } from 'src/app/core/services/i-contact.service';

@Component({
  selector: 'idel-recipient-card',
  templateUrl: './recipient-card.component.html',
  styleUrl: './recipient-card.component.scss',
})
export class RecipientCardComponent implements OnInit {
  private readonly pointsService = inject(PointsService);
  private readonly meetingsService = inject(MeetingsService);
  private readonly iContactsService = inject(IContactService);
  private readonly formOptions = inject(FormOptions);
  private readonly notifier = inject(Notifier);

  public readonly target = input.required<ActionTarget>();
  public readonly recipient = input.required<PointRecipient | MeetingRecipient>();

  public readonly refresh = output<void>();

  public readonly recipientName = signal<string>('Inconnu');
  public readonly demanderName = signal<string>('Inconnu');
  public readonly loading = signal<boolean>(false);

  ngOnInit(): void {
    this.setDemanderAndRecipientNames();
  }

  private setDemanderAndRecipientNames(): void {
    if (this.recipient().demander.id === null) {
      this.demanderName.set('Système');
    } else {
      const foundUser = this.formOptions.users().find((user) => user.userId === this.recipient().demander.id);

      if (foundUser) {
        this.demanderName.set(`${foundUser.nom.toUpperCase()} ${foundUser.prenom}`);
      }
    }

    if (this.recipient().recipient.kind === 'user') {
      const source = this.recipient().recipient.fromIadmin
        ? this.iContactsService.getContactAdmin(this.recipient().recipient.id)
        : this.iContactsService.getContact(this.recipient().recipient.id);
      source.subscribe({
        next: (recipient: BaseContact) => {
          if (recipient) {
            this.recipientName.set(`${recipient.nom!.toUpperCase()} ${recipient.prenom}`);
          }
        },
      });
    } else if (this.recipient().recipient.kind === 'group') {
      const foundGroup = this.formOptions.groups().find((group) => group.id === this.recipient().recipient.id);
      this.recipientName.set(foundGroup ? foundGroup.description : 'Inconnu');
    } else if (this.recipient().recipient.kind === 'service') {
      const foundService = this.formOptions.services().find((service) => service.id === this.recipient().recipient.id);
      this.recipientName.set(foundService ? foundService.description : 'Inconnu');
    }
  }

  public deleteRecipient(): void {
    const recipient = this.recipient();

    if (this.loading()) return;

    this.loading.set(true);

    if (this.target().scope === 'point') {
      this.pointsService
        .deletePointRecipients(this.target().id, recipient.recipient.detailId)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.notifier.showSuccess(
              new NotificationSuccess({ message: 'Le destinataire a été supprimé avec succès' })
            );
            this.refresh.emit();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({ message: 'Une erreur est survenue lors de la suppression du destinataire.' })
            );
          },
        });
    } else {
      this.meetingsService
        .deleteMeetingRecipients(this.target().id, recipient.recipient.detailId)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.notifier.showSuccess(
              new NotificationSuccess({ message: 'Le destinataire a été supprimé avec succès' })
            );
            this.refresh.emit();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({ message: 'Une erreur est survenue lors de la suppression du destinataire.' })
            );
          },
        });
    }
  }

  public setRecipientAsMain(): void {
    const recipient = this.recipient();

    if (this.loading()) return;

    if (recipient.mainRecipient) {
      this.notifier.showInfo(new NotificationInfo({ message: 'Le destinataire est déjà défini comme principal.' }));
      return;
    }

    this.loading.set(true);

    if (this.target().scope === 'point') {
      this.pointsService
        .setPointRecipientAsDefault(this.target().id, recipient.recipient.detailId)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.notifier.showSuccess(
              new NotificationSuccess({ message: 'Le destinataire a été défini comme principal' })
            );
            this.refresh.emit();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({
                message: 'Une erreur est survenue lors de la définition du destinataire comme principal.',
              })
            );
          },
        });
    } else {
      this.meetingsService
        .setMeetingRecipientAsDefault(this.target().id, recipient.recipient.detailId)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.notifier.showSuccess(
              new NotificationSuccess({ message: 'Le destinataire a été défini comme principal' })
            );
            this.refresh.emit();
          },
          error: () => {
            this.notifier.showError(
              new NotificationError({
                message: 'Une erreur est survenue lors de la définition du destinataire comme principal.',
              })
            );
          },
        });
    }
  }
}
